import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { getBaseUrl } from '../utils/getBaseUrl';
import { isUnderBaseDomain } from '../utils/brandHost';

/**
 * CmsSiteContext — resolves whether the current institute should serve
 * the public CMS marketing site (i.e. plan.cmsWebsite === true).
 *
 * Called once on app boot, no auth required. Mirrors the X-Custom-Domain
 * header pattern used by BrandingContext / apiClient so the BE
 * tenantResolver can pick the institute by host.
 *
 * Endpoint: GET /api/institute/site-config
 *   → { cmsEnabled: boolean, instituteId, instituteName }
 */
const CmsSiteContext = createContext({
  cmsEnabled: false,
  audienceMode: 'student',
  cmsTheme: {},
  cmsNavbar: {},
  cmsFooter: {},
  loading: true,
});

export const useCmsSite = () => useContext(CmsSiteContext);

export const CmsSiteProvider = ({ children }) => {
  const [state, setState] = useState({
    cmsEnabled: false,
    instituteId: null,
    instituteName: null,
    seo: {},
    audienceMode: 'student',
    cmsTheme: {},
    cmsNavbar: {},
    cmsFooter: {},
    loading: true,
  });

  useEffect(() => {
    const ctrl = new AbortController();
    const hostname = window.location.hostname;
    const headers = {};

    // Dev override: allow testing CMS on localhost by pointing at a real institute.
    //   1. http://vc.localhost:3200    → auto-extract "vc" as subdomain
    //   2. http://localhost:3200       → use REACT_APP_CMS_DEV_SUBDOMAIN from .env.local
    const devSubdomain = process.env.REACT_APP_CMS_DEV_SUBDOMAIN;
    if (hostname.endsWith('.localhost')) {
      const sub = hostname.slice(0, -('.localhost'.length));
      if (sub) headers['X-Institute-Subdomain'] = sub;
    } else if (hostname === 'localhost' && devSubdomain) {
      headers['X-Institute-Subdomain'] = devSubdomain;
    } else if (
      hostname !== 'localhost' &&
      !isUnderBaseDomain(hostname)
    ) {
      headers['X-Custom-Domain'] = hostname;
    }

    axios
      .get(`${getBaseUrl()}/institute/site-config`, { headers, signal: ctrl.signal, timeout: 8000 })
      .then((res) => {
        const d = res?.data || {};
        setState({
          cmsEnabled: !!d.cmsEnabled,
          instituteId: d.instituteId || null,
          instituteName: d.instituteName || null,
          seo: d.seo || {},
          audienceMode: d.audienceMode === 'employee' ? 'employee' : 'student',
          cmsTheme: d.theme && typeof d.theme === 'object' ? d.theme : {},
          cmsNavbar: d.navbar && typeof d.navbar === 'object' ? d.navbar : {},
          cmsFooter: d.footer && typeof d.footer === 'object' ? d.footer : {},
          loading: false,
        });
      })
      .catch((err) => {
        // Ignore abort errors (React StrictMode double-mount cleanup, or
        // unmount). Otherwise the unmount-triggered abort flips loading→false
        // with cmsEnabled=false BEFORE the re-mounted effect's request can
        // resolve, causing the root route to redirect to /login.
        const isAbort =
          axios.isCancel?.(err) ||
          err?.name === 'CanceledError' ||
          err?.name === 'AbortError' ||
          err?.code === 'ERR_CANCELED';
        if (isAbort) return;
        setState((s) => ({ ...s, cmsEnabled: false, loading: false }));
      });

    return () => ctrl.abort();
  }, []);

  return <CmsSiteContext.Provider value={state}>{children}</CmsSiteContext.Provider>;
};
